import _ from "lodash";
import { translate } from "../../Translations/translatorService";
import { LEGALBIRD_BASE_URL } from "../../../config/_entrypoint";
import { userHasRole } from "../../backofficeUserService";
import { BackofficeUser, Roles } from "../../../types/BackofficeUser";
import { Badge } from "../../../types/Badge";
import { BackofficeCase } from "../../../types/BackofficeCase";
import { ProductStage } from "../../../types/ProductStage";
import { CourtAppointment } from "../../../types/CourtAppointment";
import { alimony } from "./alimony";
import { dataLeakContract } from "./dataLeakContract";
import { death } from "./death";
import { divorce } from "./divorce";
import { eventContract } from "./eventContract";
import { familyLawCase } from "./familyLawCase";
import { generalCivilLaw } from "./generalCivilLaw";
import { civilServiceLaw } from "./civilServiceLaw";
import { membershipContract } from "./membershipContract";
import { otherContract } from "./otherContract";
import { patientDecree } from "./patientDecree";
import { pendingCase } from "./pendingCase";
import { powerOfAttorney } from "./powerOfAttorney";
import { providerContract } from "./providerContract";
import { purchaseContract } from "./purchaseContract";
import { serviceContract } from "./serviceContract";
import { settlement } from "./settlement";
import { shortTimeWork } from "./shortTimeWork";
import { employmentContract } from "./employmentContract";
import { rentalContract } from "./rentalContract";
import { subscriptionContract } from "./subscriptionContract";
import { traffic } from "./traffic";
import { trafficAccident } from "./trafficAccident";
import { travelContract } from "./travelContract";
import { vehicleContract } from "./vehicleContract";
import { ProductUnion } from "../../../types/ProductUnion";
import { StageMetaData } from "../../../types/ProductService/ProductService";
import dayjs from "dayjs";

type ProductDataType = typeof productData;
export type ProductDataValue = ProductDataType[keyof ProductDataType];

const SERVICE_WORLD_BASE_LINK = LEGALBIRD_BASE_URL + "/servicewelt/";

const productData = {
  alimony,
  civilServiceLaw,
  dataLeakContract,
  death,
  divorce,
  eventContract,
  familyLawCase,
  generalCivilLaw,
  membershipContract,
  otherContract,
  patientDecree,
  pendingCase,
  powerOfAttorney,
  providerContract,
  purchaseContract,
  serviceContract,
  settlement,
  shortTimeWork,
  employmentContract,
  rentalContract,
  subscriptionContract,
  traffic,
  trafficAccident,
  travelContract,
  vehicleContract,
};

const getProductHeadline = (urlPart: typeof productData[keyof ProductDataType]["urlPart"]) => {
  const productClassName = getProductName(urlPart);
  if (!productClassName) {
    return "";
  }
  return getProductLabel(productClassName);
};

const getProductName = (urlPart: typeof productData[keyof ProductDataType]["urlPart"]) => {
  return _.findKey(productData, ["urlPart", urlPart]) as ProductUnion["productClassName"];
};

const getProductNameBy = (path: any, value: any): ProductUnion["productClassName"] => {
  // @ts-ignore
  return _.findKey(productData, [path, value]);
};

const getProductData = <T extends keyof ProductDataValue>(
  productClassName: ProductUnion["productClassName"],
  path: string,
  defaultValue: ProductDataValue[T] | "" = ""
): ProductDataValue[T] | "" => {
  return _.get(productData, `${_.camelCase(productClassName)}.${path}`, defaultValue);
};

const getFormDefinition = (product: ProductUnion, currentUser?: BackofficeUser) => {
  const formDefinitionFunction: Function = getProductData(product.productClassName, "formDefinition", () => {});
  return formDefinitionFunction({ product: product, currentUser: currentUser });
};

const getFormDefinitionExternals = (product: ProductUnion) => {
  if (!product.stage) {
    return [];
  }
  const formDefinitionFunction = getProductData(product.productClassName, "formDefinitionExternals", () => {});
  return formDefinitionFunction({ product: product });
};

const getFullFormDefinition = (product: ProductUnion, currentUser: BackofficeUser) => {
  const formDefinitionFunction = getProductData(product.productClassName, "fullFormDefinition", () => {});
  return formDefinitionFunction({ product: product, currentUser: currentUser });
};

const getProductSpecificDefinition = (product: ProductUnion) => {
  const productSpecificDefinition = getProductData(product.productClassName, "productSpecificDefinition", () => {});
  return productSpecificDefinition({ product: product });
};

const getResponsibleInstitutes = (product: ProductUnion) => {
  let courtPath = "";
  let authorityPath = "";
  let prosecutionPath = "";

  switch (product.productClassName) {
    case "divorce":
      courtPath = "responsibleCourt";
      break;
    case "traffic":
      courtPath = "responsibleCourt";
      authorityPath = "responsibleAuthority";
      prosecutionPath = "responsibleProsecution";
      break;
    case "settlement":
      courtPath = "responsibleCourt";
      break;
    case "trafficAccident":
      courtPath = "responsibleCourt";
      authorityPath = "responsibleAuthority";
      break;
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "rentalContract":
    case "employmentContract":
      courtPath = "responsibleCourt";
      prosecutionPath = "responsibleProsecution";
      break;
    case "generalCivilLaw":
    case "civilServiceLaw":
      courtPath = "responsibleCourt";
      authorityPath = "responsibleAuthority";
      prosecutionPath = "responsibleProsecution";
      break;
    default:
      break;
  }
  return { courtPath, authorityPath, prosecutionPath };
};

const getBadgesMinimal = (product: ProductUnion) => {
  return [
    {
      text: getProductData(product.productClassName, "label"),
      isActive: true,
    },
  ];
};

const getCaseInstanceLevel = (productEntity: ProductUnion, depth = 0) => {
  if (productEntity.isHigherInstanceCase) {
    depth++;
    depth = getCaseInstanceLevel(productEntity.previousInstanceCase, depth);
  }
  return depth;
};

const getBadges = (product: ProductUnion, currentUser?: BackofficeUser) => {
  const getCaseIdentifier = (productClassName: ProductUnion["productClassName"], id: number) => {
    return productClassName === "traffic" ? " " + id : "";
  };

  let badges: Badge[] = [
    {
      text: getProductData(product.productClassName, "label"),
      isActive: true,
      lbProductId: product.id,
    },
  ];

  if (product.isHigherInstanceCase) {
    const instanceLevelLabels: Record<number, string> = {
      1: "Berufungsfall",
      2: "Revisionsfall",
    };

    badges.push({
      text: instanceLevelLabels[getCaseInstanceLevel(product)] || "Höherinstanzlicher Fall",
      isActive: true,
    });
    badges.push({
      text: "Vorherige Instanz",
      isActive: false,
      lbProductId: product.id,
      linkTo: getCaseLink(
        {
          productClassName: product.previousInstanceCase.name,
          id: product.previousInstanceCase.id,
        },
        currentUser && userHasRole(currentUser, Roles.callcenter) ? "/callcenter" : ""
      ),
    });
  }

  if (product.nextInstanceCaseBaseData) {
    badges.push({
      text: "Nächste Instanz",
      isActive: false,
      lbProductId: product.id,
      linkTo: getCaseLink(
        {
          productClassName: product.nextInstanceCaseBaseData.product,
          id: product.nextInstanceCaseBaseData.id,
        },
        currentUser && userHasRole(currentUser, Roles.callcenter) ? "/callcenter" : ""
      ),
    });
  }

  if (product.backofficeCase && product.backofficeCase.relatedCases) {
    _.forEach(product.backofficeCase.relatedCases, (relatedBackofficeCase) => {
      const productClassName = getProductNameBy("lbProductClass", relatedBackofficeCase.lbProductClass);

      if (productClassName) {
        badges.push({
          text:
            getProductLabel(productClassName) + getCaseIdentifier(productClassName, relatedBackofficeCase.lbProductId),
          isActive: false,
          lbProductId: relatedBackofficeCase.lbProductId,
          linkTo: getCaseLink(
            {
              productClassName,
              id: relatedBackofficeCase.lbProductId,
            },
            currentUser && userHasRole(currentUser, Roles.callcenter) ? "/callcenter" : ""
          ),
        });
      }

      badges = _.orderBy(badges, "lbProductId", "desc");
    });
  }

  const productSpecificBadges = getProductData(product.productClassName, "productSpecificBadges")({ product });
  badges = badges.concat(productSpecificBadges);

  return badges;
};

const getIsApplicationComplete = (product: ProductUnion) => {
  return product.leadStatus >= getProductData(product.productClassName, "minimumCaseStatus");
};

const getIsReady = (product: ProductUnion) => {
  return product.stage === getProductData(product.productClassName, "setBackCaseStage");
};

const getStageList = (productClassName: ProductUnion["productClassName"]) => {
  return getProductData(productClassName, "stages");
};

const getProductStageList = (product: ProductUnion) => {
  if (!_.has(product, "leadStatusList")) {
    return getStageList(product.productClassName);
  }
  return _.keyBy(
    _.filter(getStageList(product.productClassName), (stage) => {
      return _.includes(product.leadStatusList, stage.leadStatus) || stage.stage === 0;
    }),
    "stage"
  );
};

const getCaseLabel = (product: ProductUnion) => {
  if (!product.petitioner?.givenName) {
    return product.reference;
  }
  return (
    translate(`${product.productClassName}.petitioner.gender.values.${product.petitioner.gender}`, "") +
    (product.petitioner.gender ? " " : "") +
    product.petitioner.givenName +
    " " +
    product.petitioner.familyName
  );
};

const getProductLabel = (productClassName: ProductUnion["productClassName"]) => {
  return getProductData(productClassName, "label");
};

const getProductCategory = (product: Pick<ProductUnion, "productCategory" | "productClassName">) => {
  return _.capitalize(translate("general.productCategory." + product.productCategory));
};

const getFullProductLabel = (product: Pick<ProductUnion, "productCategory" | "productClassName">) => {
  return getProductCategory(product) + ": " + getProductLabel(product.productClassName);
};

const getCaseLink = (product: Pick<ProductUnion, "productClassName" | "id">, subFolder = "") => {
  return "/services" + subFolder + "/fall/" + getProductData(product.productClassName, "urlPart") + "/" + product.id;
};

const getCaseLinkByBackofficeCase = (backofficeCase: BackofficeCase, subFolder = "") => {
  const productClassName = getProductNameBy("lbProductClass", backofficeCase.lbProductClass);

  if (!productClassName) {
    return "/";
  }

  const product = {
    productClassName: productClassName,
    id: backofficeCase.lbProductId,
  };
  return getCaseLink(product, subFolder);
};


const getCaseDetailsSpecialActions = (product: ProductUnion) => {
  return getProductData(product.productClassName, "caseDetailsSpecialActions", () => null)({ product });
};

const getAdvoAssistRepresentative = (product: ProductUnion) => {
  const appointments = _.get(product, "courtAppointments", []);
  //same order as in CourtAppointments.tsx
  const latestAppointment = _.head(_.sortBy(appointments, (c) => -c.id));
  return latestAppointment?.advoAssist?.partnerLawyer;
};

const getNextStage = (product: ProductUnion): StageMetaData[number] | null => {
  const orderedStages = _.sortBy(getProductData(product.productClassName, "stages"), "stage");
  const currentStage = _.findIndex(orderedStages, (orderedStage) => {
    return orderedStage.stage === product.stage;
  });
  const nextStage = currentStage + 1;
  for (let i = nextStage; i < orderedStages.length; i++) {
    if (orderedStages[i].isClickable({ product })) {
      return orderedStages[i];
    }
  }
  return null;
};

const getLeadStatusByStage = (productClassName: ProductUnion["productClassName"], stage: ProductStage["stage"]) => {
  return getStageList(productClassName)[stage].leadStatus;
};

const isDocumentProduct = (productClassName: ProductUnion["productClassName"]) => {
  return _.includes(
    ["shortTimeWork", "patientDecree", "powerOfAttorney", "documents", "death", "alimony"],
    productClassName
  );
};

const getServiceWorldCaseLink = (product: ProductUnion, hash?: string) => {
  const uri =
    SERVICE_WORLD_BASE_LINK +
    translate("general.productCategory." + product.productCategory) +
    "/" +
    getProductData(product.productClassName, "lbUrlPart") +
    "/" +
    product.id;
  if (hash) {
    return uri + "#" + hash;
  }
  return uri;
};

const getServiceWorldCaseMessagingLink = (product: ProductUnion) => {
  return `${SERVICE_WORLD_BASE_LINK}nachrichten/neu?product=${product.productClassName}&productId=${product.id}`;
};

//in the future use productCategory === "contractLaw" instead
const isContractProduct = (productClassName: ProductUnion["productClassName"]) => {
  return _.includes(
    [
      "eventContract",
      "membershipContract",
      "otherContract",
      "providerContract",
      "dataLeakContract",
      "purchaseContract",
      "serviceContract",
      "subscriptionContract",
      "travelContract",
      "vehicleContract",
      "rentalContract",
      "civilServiceLaw",
    ],
    productClassName
  );
};

const hasActiveCourtAppointment = (courtAppointments: CourtAppointment[]) => {
  return _.some(courtAppointments, (courtAppointment) => {
    const appointmentDate = dayjs(courtAppointment?.courtDate?.appointmentDate);
    return appointmentDate.isValid() && appointmentDate.isSameOrAfter(new Date(), "day") && courtAppointment.advoAssist;
  });
};

export {
  productData,
  getLeadStatusByStage,
  getCaseLink,
  getCaseLinkByBackofficeCase,
  getCaseDetailsSpecialActions,
  getProductLabel,
  getProductCategory,
  getFullProductLabel,
  getCaseLabel,
  getStageList,
  getProductStageList,
  getProductHeadline,
  getProductData,
  getProductName,
  getFormDefinition,
  getFullFormDefinition,
  getProductSpecificDefinition,
  getBadges,
  getBadgesMinimal,
  getIsApplicationComplete,
  getNextStage,
  getProductNameBy,
  getIsReady,
  isDocumentProduct,
  isContractProduct,
  getResponsibleInstitutes,
  getFormDefinitionExternals,
  getServiceWorldCaseLink,
  getAdvoAssistRepresentative,
  hasActiveCourtAppointment,
  getServiceWorldCaseMessagingLink,
};
